import React from 'react';
import {
  Box,
  Text,
  Amount,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableToolbar,
  TablePagination,
} from '@razorpay/blade/components';
import { YTDSummaryComponentData } from './types';
import { usePagination } from 'hooks/usePagination';
import { getTableItemCountLimits } from 'components/SalaryComponents/utils';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { paiseAmountToRupee } from 'utils/Numbers';

interface YTDSummaryTableProps {
  data: YTDSummaryComponentData[];
  isLoading?: boolean;
}

export const YTDSummaryTable = ({ data, isLoading = false }: YTDSummaryTableProps) => {
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const isTableEmpty = data.length === 0;

  return (
    <Box marginTop={'spacing.4'}>
      <Box overflowX="auto" maxWidth="100vw">
        <Table
          data={{ nodes: data.map((item) => ({ id: item.component_name, ...item })) }}
          isLoading={isLoading}
          pagination={
            !isTableEmpty ? (
              <TablePagination
                currentPage={currentPage}
                showPageSizePicker={true}
                onPageChange={({ page }) => {
                  setCurrentPage(page);
                }}
                onPageSizeChange={({ pageSize }) => {
                  setItemsPerPage(pageSize);
                }}
                totalItemCount={data.length}
              />
            ) : undefined
          }
          toolbar={
            <TableToolbar
              title={getTableItemCountLimits(
                data.length,
                currentPage,
                itemsPerPage,
                'FBP Reimbursement',
              ).replace('components', '')}
            />
          }
          showBorderedCells={false}>
          {(summaryData) => (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>
                    <Text weight="semibold">Benefit type</Text>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Box display="flex" justifyContent="end">
                      <Text weight="semibold">Accrued</Text>
                    </Box>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Box display="flex" justifyContent="end">
                      <Text weight="semibold">Claimed</Text>
                    </Box>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Box display="flex" justifyContent="end">
                      <Text weight="semibold">Approved</Text>
                    </Box>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Box display="flex" justifyContent="end">
                      <Text weight="semibold">Processed</Text>
                    </Box>
                  </TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {summaryData.map((row, index) => (
                  <TableRow key={`${row.component_name}-${index}`} item={row}>
                    <TableCell>
                      <Text>{row.component_name}</Text>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" justifyContent="end">
                        <Amount value={paiseAmountToRupee(row.amount_accrued_till_date)} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" justifyContent="end">
                        <Amount value={paiseAmountToRupee(row.amount_claimed_till_date)} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" justifyContent="end">
                        <Amount value={paiseAmountToRupee(row.amount_approved_till_date)} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" justifyContent="end">
                        <Amount value={paiseAmountToRupee(row.amount_processed_till_date)} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
      </Box>
      {isTableEmpty && !isLoading && (
        <EmptyListView
          title="No FBP reimbursement data found"
          description={
            <Text color="surface.text.gray.muted" weight="medium" textAlign="center">
              There are no flexible benefit declarations or claims to display
            </Text>
          }
        />
      )}
    </Box>
  );
};
